import React, { useEffect, useRef, useCallback } from 'react';
import { useVisualizationStore } from '../store/visualizationStore';
import * as d3 from 'd3';

export const ZoomTransition: React.FC = () => {
  const transitionRef = useRef<any>(null);
  const {
    viewport,
    zoomLevels,
    setViewport
  } = useVisualizationStore();

  // Create smooth zoom transition between levels
  const createZoomTransition = useCallback((
    targetZoom: number,
    targetX: number,
    targetY: number,
    duration: number = 750
  ) => {
    // Cancel any existing transition
    if (transitionRef.current) {
      transitionRef.current = null;
    }

    const startZoom = viewport.zoom;
    const startX = viewport.x;
    const startY = viewport.y;

    // Create D3 transition
    const transition = d3.transition()
      .duration(duration)
      .ease(d3.easeCubicInOut);

    transitionRef.current = transition;

    transition.tween('zoom', () => {
      const interpolateZoom = d3.interpolate(startZoom, targetZoom);
      const interpolateX = d3.interpolate(startX, targetX);
      const interpolateY = d3.interpolate(startY, targetY);

      return (t: number) => {
        setViewport({
          zoom: interpolateZoom(t),
          x: interpolateX(t),
          y: interpolateY(t)
        });
      };
    });

    return transition.end().catch(() => {
      // Transition was interrupted, which is normal
      return Promise.resolve();
    });
  }, [viewport, setViewport]);

  // Enhanced zoom to level with smooth transitions
  const zoomToLevel = useCallback((level: number, centerX?: number, centerY?: number) => {
    if (level < 0 || level >= zoomLevels.length) return;

    const targetLevel = zoomLevels[level];
    const targetZoom = (targetLevel.minZoom + targetLevel.maxZoom) / 2;
    
    // Calculate target position (center on viewport or specific point)
    const targetX = centerX ?? viewport.x;
    const targetY = centerY ?? viewport.y;

    return createZoomTransition(targetZoom, targetX, targetY);
  }, [zoomLevels, viewport, createZoomTransition]);

  // Zoom to specific node with appropriate level
  const zoomToNode = useCallback((nodeId: string, nodeX: number, nodeY: number, nodeType: string) => {
    // Determine target level based on node type
    let targetLevel = 0;
    switch (nodeType) {
      case 'site':
        targetLevel = 1;
        break;
      case 'pod':
        targetLevel = 2;
        break;
      case 'rack':
        targetLevel = 3;
        break;
      case 'device':
        targetLevel = 4;
        break;
      case 'component':
        targetLevel = 4;
        break;
    }

    if (targetLevel >= zoomLevels.length) return;

    const level = zoomLevels[targetLevel];
    const targetZoom = (level.minZoom + level.maxZoom) / 2;
    
    // Center on the node
    const targetX = window.innerWidth / 2 - nodeX * targetZoom;
    const targetY = window.innerHeight / 2 - nodeY * targetZoom;

    return createZoomTransition(targetZoom, targetX, targetY, 1000);
  }, [zoomLevels, createZoomTransition]);

  // Smooth pan to coordinates
  const panTo = useCallback((x: number, y: number, duration: number = 500) => {
    return createZoomTransition(viewport.zoom, x, y, duration);
  }, [viewport.zoom, createZoomTransition]);

  // Zoom in/out with smooth transition
  const zoomBy = useCallback((factor: number, centerX?: number, centerY?: number) => {
    const newZoom = Math.max(0.1, Math.min(10, viewport.zoom * factor));
    
    // If center point provided, zoom towards/away from that point
    let targetX = viewport.x;
    let targetY = viewport.y;
    
    if (centerX !== undefined && centerY !== undefined) {
      const zoomDelta = newZoom / viewport.zoom;
      targetX = centerX - (centerX - viewport.x) * zoomDelta;
      targetY = centerY - (centerY - viewport.y) * zoomDelta;
    }

    return createZoomTransition(newZoom, targetX, targetY, 300);
  }, [viewport, createZoomTransition]);

  // Fit content to viewport
  const fitToView = useCallback((
    minX: number, maxX: number, 
    minY: number, maxY: number,
    padding: number = 50
  ) => {
    const contentWidth = maxX - minX;
    const contentHeight = maxY - minY;
    const viewportWidth = window.innerWidth - padding * 2;
    const viewportHeight = window.innerHeight - padding * 2;

    const scaleX = viewportWidth / contentWidth;
    const scaleY = viewportHeight / contentHeight;
    const targetZoom = Math.min(scaleX, scaleY, 10); // Cap at max zoom

    const contentCenterX = (minX + maxX) / 2;
    const contentCenterY = (minY + maxY) / 2;
    const targetX = window.innerWidth / 2 - contentCenterX * targetZoom;
    const targetY = window.innerHeight / 2 - contentCenterY * targetZoom;

    console.log('fitToView calculation:', {
      bounds: { minX, maxX, minY, maxY },
      contentSize: { contentWidth, contentHeight },
      viewportSize: { viewportWidth, viewportHeight },
      scales: { scaleX, scaleY, targetZoom },
      contentCenter: { contentCenterX, contentCenterY },
      target: { targetX, targetY },
      window: { width: window.innerWidth, height: window.innerHeight }
    });

    return createZoomTransition(targetZoom, targetX, targetY, 1000);
  }, [createZoomTransition]);

  // Expose methods to store for global access
  useEffect(() => {
    // Add transition methods to the store
    const store = useVisualizationStore.getState();
    
    // Extend store with transition methods
    (store as any).zoomTransition = {
      zoomToLevel,
      zoomToNode,
      panTo,
      zoomBy,
      fitToView,
      createTransition: createZoomTransition
    };
  }, [zoomLevels, createZoomTransition, fitToView, panTo, zoomBy, zoomToLevel, zoomToNode]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (transitionRef.current) {
        transitionRef.current = null;
      }
    };
  }, []);

  // This component doesn't render anything visible
  return null;
};

// Hook for using zoom transitions
export const useZoomTransition = () => {
  const store = useVisualizationStore();
  
  return {
    zoomToLevel: (level: number, centerX?: number, centerY?: number) => {
      const transitions = (store as any).zoomTransition;
      return transitions?.zoomToLevel(level, centerX, centerY);
    },
    zoomToNode: (nodeId: string, nodeX: number, nodeY: number, nodeType: string) => {
      const transitions = (store as any).zoomTransition;
      return transitions?.zoomToNode(nodeId, nodeX, nodeY, nodeType);
    },
    panTo: (x: number, y: number, duration?: number) => {
      const transitions = (store as any).zoomTransition;
      return transitions?.panTo(x, y, duration);
    },
    zoomBy: (factor: number, centerX?: number, centerY?: number) => {
      const transitions = (store as any).zoomTransition;
      return transitions?.zoomBy(factor, centerX, centerY);
    },
    fitToView: (minX: number, maxX: number, minY: number, maxY: number, padding?: number) => {
      const transitions = (store as any).zoomTransition;
      return transitions?.fitToView(minX, maxX, minY, maxY, padding);
    }
  };
};